// built-from: /contextual-facilitator-companion v2 impl:contextual-facilitator-companion role:implementation
import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import ArcPilotShell from './components/ArcPilotShell';
import CaptureShell from './components/CaptureShell';
import DeliveryView from './components/DeliveryView';
import MigrationUnitView from './components/MigrationUnitView';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import ScopeWidget from './components/ScopeWidget';
import AppShell from './components/AppShell';
import ProgrammePlaceholder from './components/ProgrammePlaceholder';
import VisionFacilitationScreen from './facilitation/VisionFacilitationScreen';
import OrganisationContext from './components/OrganisationContext';
import KnowledgeRepoView from './components/KnowledgeRepoView';
import FirstRunGateway from './components/FirstRunGateway';
import StreamMappingHub from './components/StreamMappingHub';
import { EngagementCoordinationJourney } from './engagement/EngagementCoordinationJourney';
import { ValueStreamMappingActivityWorkspace } from './engagement/ValueStreamMappingActivityWorkspace';
import { isBetaEnabled, isLegacySurfacesEnabled } from './utils/featureFlags';
import { isAuthed, setAuthed } from './utils/authSession';
import { deriveProgramme, deriveValueProven } from './utils/programmeModel';
import { readOrgContext } from './utils/orgContext';
import { readScope } from './utils/scope';
import { readVision } from './utils/vision';
import { EngagementProvider, useEngagement, getEngagementSnapshot } from './hooks/useEngagement';
import { useEngagementSetup } from './hooks/useEngagementSetup';
import { engagementActivityDestination } from './engagement/engagementSpecialists';
import type { ProgrammeFacts } from './utils/programmeModel';
import './App.css';

// The "/" dashboard route content (027 B2): a thin consumer of the
// EngagementProvider. It subscribes to the provider's reactive `snapshot` so the
// dashboard re-derives whenever the current engagement resolves / switches /
// saves; the three readers (readOrgContext / readScope / readVision) then read
// the freshly-published module-level snapshot synchronously (the runtime bridge
// to the pure utils). When no engagement is resolved the snapshot is null and the
// readers keep their empty-shape floor (falling back to legacy localStorage).
const DashboardRoute: React.FC = () => {
  // Subscribe to the provider's reactive snapshot: this re-renders the dashboard
  // when the engagement resolves, after which the readers below see the published
  // module snapshot. The value itself is not read directly — the readers are the
  // single source for the derived facts (no second source of truth).
  useEngagement();

  // Real org-context facts for the dashboard read-side (022 FR-009): the
  // org-setup warm-up to-do surfaces until an org is captured and its background
  // is confirmed. Read from the resolved engagement.
  const orgContext = readOrgContext();
  const orgFacts = {
    organisation: orgContext.organisation ? { name: orgContext.organisation.name } : null,
    companyBackgroundConfirmed: orgContext.background?.confirmed === true,
  };

  // Real captured scope (023 FR-004/FR-009): the persisted "Areas of the
  // business" reach the cockpit so the Frame gate, the unlock rail and the
  // checklist reflect REAL scope. Re-read on every render; scope is now edited on
  // the /streams route (028 Increment C), so the dashboard recomputes from the
  // freshly-persisted snapshot on its next render rather than via a live counter.
  const scopeAreas = readScope().areas;

  // Real captured vision (024 FR-007/FR-008): the persisted Vision record drives
  // the dashboard goal — visionCaptured from the captured goals, and the
  // CONFIRMED Cover Story headline ONLY when its `confirmed` flag is set. An
  // unconfirmed / absent cover story yields NO vision fact, so the read model's
  // `goalsCapturedUnconfirmed` drives the honest "Confirm your Cover Story
  // headline" prompt — no hardcoded headline.
  const vision = readVision();
  const visionFacts =
    vision.coverStory?.confirmed === true
      ? {
          visionCaptured: vision.goals.length > 0,
          vision: {
            coverStory: vision.coverStory.headline,
            statedBy: 'sponsor',
            confirmed: true,
          },
        }
      : { visionCaptured: vision.goals.length > 0 };

  // Real knowledge-repo link (032, FR-005/FR-006): the persisted repoLink reaches
  // the MODEL that decides setup-completeness so the REQUIRED `setup-repo` item
  // reads done once a repo is linked. Read from the SAME provider snapshot the
  // display path uses (Dashboard.readRepoLinkFacts → getEngagementSnapshot), so
  // the row's display summary and the completeness model agree. Absent → the
  // unlinked floor (the field is omitted, matching the snapshot's optional shape).
  const repoLink = getEngagementSnapshot()?.repoLink as ProgrammeFacts['repoLink'] | undefined;
  const repoLinkFacts: Pick<ProgrammeFacts, 'repoLink'> = repoLink ? { repoLink } : {};

  // The REAL fact set the dashboard derives against (029 F): org-context + the
  // captured areas + the captured vision + the knowledge-repo link. NO hardcoded
  // streams / failure cases — the value board's illustrative parts come from the
  // labelled fixture, its REAL parts from these facts (vision goals + areas).
  const facts = { ...orgFacts, areas: scopeAreas, streams: [], ...visionFacts, ...repoLinkFacts };

  // The REAL vision goals that drive progress-to-vision (029 F): each captured
  // goal carries the confidence we hold in it — higher once the Cover Story is
  // confirmed. No fabrication; the headline measure reads 0 on no goals.
  const coverStoryConfirmed = readVision().coverStory?.confirmed === true;
  const visionGoals = readVision().goals.map((g) => ({
    id: g.id,
    text: g.text,
    confidence: coverStoryConfirmed ? 0.8 : 0.4,
  }));

  return (
    <AppShell>
      <Dashboard
        state="ready"
        model={deriveProgramme(facts)}
        valueProven={deriveValueProven({ ...facts, visionGoals })}
      />
    </AppShell>
  );
};

// 054-facilitated-vision (T046/T052): the facilitated-session screen — the
// The Vision screen owns its working surface and attaches the ONE facilitator
// rail. Since the Phase E cutover this IS the vision experience (the legacy
// screen and its route are gone). The shell keys off the facilitation
// SESSION id (the fact-event log's partition), not the engagement id.
const FacilitatedSessionRoute: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  if (!sessionId) return <Navigate to="/" replace />;
  return <VisionFacilitationScreen sessionId={sessionId} />;
};

const LegacyStreamVsmRedirect: React.FC = () => {
  const { streamId } = useParams<{ streamId: string }>();
  if (!streamId) return <Navigate to="/streams" replace />;
  return <Navigate to={`/streams/${encodeURIComponent(streamId)}/workspace?view=vsm`} replace />;
};

const EngagementScopedDestination: React.FC<{ tail: string }> = ({ tail }) => {
  const model = useEngagementSetup();
  const location = useLocation();
  if (tail === 'brief' || tail === '') return <Navigate to="/engagement/brief" replace />;
  const match = /^activities\/([^/]+)$/.exec(tail);
  if (!match) return <div role="alert">This engagement link is not available.</div>;
  if (model.status === 'loading') return <div role="status">Opening activity…</div>;
  const activityId = decodeURIComponent(match[1]);
  const activity = model.setup?.activities.find((candidate) => candidate.id === activityId);
  if (!activity) return <div role="alert">This activity is not in the selected engagement.</div>;
  const destination = engagementActivityDestination(activity);
  return <Navigate to={`${destination}${location.search}`} replace />;
};

const EngagementScopedLink: React.FC = () => {
  const params = useParams<{ engagementId: string; '*': string }>();
  const { currentEngagementId, switchEngagement } = useEngagement();
  const [failure, setFailure] = useState(false);
  const engagementId = params.engagementId || '';
  const tail = params['*'] || 'brief';

  useEffect(() => {
    let active = true;
    setFailure(false);
    if (!engagementId) return () => { active = false; };
    const select = currentEngagementId === engagementId
      ? Promise.resolve(true)
      : switchEngagement(engagementId);
    void select.then((selected) => { if (active && !selected) setFailure(true); });
    return () => { active = false; };
  }, [currentEngagementId, engagementId, switchEngagement]);

  if (failure) return <div role="alert">This engagement is unavailable.</div>;
  if (currentEngagementId !== engagementId) return <div role="status">Opening engagement…</div>;
  return <EngagementScopedDestination tail={tail} />;
};

// The engagement-scoped surface (027 Slice C): inside the provider, when the
// practitioner has NO engagement (isFirstRun) we render ONLY the centred
// FirstRunGateway — never an empty/lying dashboard or the switcher chrome
// (SC-003, User Story 1). Once the gateway creates the first engagement,
// isFirstRun flips false and the scoped route tree renders.
const ScopedApp: React.FC = () => {
  const { isFirstRun, createEngagement } = useEngagement();
  const legacySurfacesEnabled = isLegacySurfacesEnabled();
  const location = useLocation();

  if (isFirstRun && !location.pathname.startsWith('/engagements/')) {
    return <FirstRunGateway onCreate={createEngagement} />;
  }

  return (
      <Routes>
      {/* 008-landing-relocation: the dashboard (slice 1) is now the / default
          (FR-004), seeded with an empty-programme read model so it shows the
          "capture the vision first" empty state. ArcPilotShell relocates to
          /landing unchanged (FR-001/FR-002); the temporary /dashboard route is
          removed (FR-010). The catch-all still lands on / (FR-007). */}
      {/* 007-dashboard-shell: the dashboard now renders inside the persistent
          AppShell (sidebar + top bar), and the nav destinations not yet built
          mount a ProgrammePlaceholder INSIDE the shell so the nav works (no
          dead ends) until those slices land. */}
      <Route path="/" element={legacySurfacesEnabled ? <DashboardRoute /> : <Navigate to="/engagement" replace />} />
      {/* Nav destinations built in later slices — placeholders inside the shell
          so the navigation has no dead ends. */}
      {/* 054-facilitated-vision (T046/T052): the facilitated vision session
          screen — the one vision experience since the Phase E cutover (the
          legacy 009 route retired with its screen; an unknown path falls
          through the catch-all to the dashboard).
          Docked: the surface + chat rail own their internal scroll. */}
      <Route path="/facilitation/:sessionId" element={<AppShell dock><FacilitatedSessionRoute /></AppShell>} />
      {/* 022-organisation-context: the "Company context" setup workspace renders
          INSIDE the AppShell (sidebar + top bar), consistent with the dashboard
          and the other programme surfaces. */}
      {legacySurfacesEnabled && <Route path="/organisation" element={<AppShell><OrganisationContext /></AppShell>} />}
      {/* 028-engagement-setup (Increment C): the candidate value streams surface
          (ScopeWidget) now lives on its own /streams route INSIDE the AppShell
          (sidebar + top bar), consistent with the dashboard and the other
          programme surfaces — moved off the dashboard inline. ScopeWidget's
          onChange is omitted: the snapshot is the source of truth, and the
          dashboard recomputes from it on its next render. */}
      {legacySurfacesEnabled && <Route path="/streams" element={<AppShell><ScopeWidget /></AppShell>} />}
      <Route path="/streams/:streamId" element={<AppShell><StreamMappingHub /></AppShell>} />
      <Route path="/streams/:streamId/workspace" element={<AppShell><ValueStreamMappingActivityWorkspace /></AppShell>} />
      {legacySurfacesEnabled && <Route path="/streams/:streamId/vsm" element={<LegacyStreamVsmRedirect />} />}
      {/* 032-knowledge-repo-link connect surface. 070 AC#6: it is reached from the
          active engagement (an activity's "Connect knowledge repo" action), so it
          is NOT gated behind legacy surfaces — it must resolve in the current
          engagement journey. An unlinked repoLink never hard-blocks any route. */}
      <Route path="/knowledge-repo" element={<AppShell><KnowledgeRepoView /></AppShell>} />
      <Route path="/engagement/*" element={<EngagementCoordinationJourney />} />
      <Route path="/engagements/:engagementId/*" element={<EngagementScopedLink />} />
      {legacySurfacesEnabled && <Route path="/domains" element={<AppShell><ProgrammePlaceholder title="Domains" /></AppShell>} />}
      {legacySurfacesEnabled && <Route path="/systems" element={<AppShell><ProgrammePlaceholder title="Systems" /></AppShell>} />}
      {legacySurfacesEnabled && <Route path="/validation" element={<AppShell><ProgrammePlaceholder title="Validation" /></AppShell>} />}
      {legacySurfacesEnabled && <Route path="/prioritisation" element={<AppShell><ProgrammePlaceholder title="Prioritisation" /></AppShell>} />}
      {legacySurfacesEnabled && <Route path="/golden-thread" element={<AppShell><ProgrammePlaceholder title="Golden thread" /></AppShell>} />}
      {legacySurfacesEnabled && <Route path="/sources" element={<AppShell><ProgrammePlaceholder title="Sources" /></AppShell>} />}
      {legacySurfacesEnabled && <Route path="/landing" element={<ArcPilotShell />} />}
      {legacySurfacesEnabled && <Route path="/sessions/:sessionId" element={<ArcPilotShell />} />}
      {legacySurfacesEnabled && <Route path="/capture" element={<CaptureShell />} />}
      {legacySurfacesEnabled && <Route path="/capture/:sessionId" element={<CaptureShell />} />}
      {legacySurfacesEnabled && isBetaEnabled() && (
        <>
          <Route path="/sessions/:sessionId/delivery" element={<DeliveryView />} />
          <Route path="/sessions/:sessionId/delivery/units/:unitName" element={<MigrationUnitView />} />
        </>
      )}
      <Route path="*" element={<Navigate to={legacySurfacesEnabled ? "/" : "/engagement"} replace />} />
      </Routes>
  );
};

const App: React.FC = () => {
  // Re-render trigger once the browser session is marked authenticated.
  const [authed, setAuthedState] = useState<boolean>(() => isAuthed());

  if (!authed) {
    return (
      <LoginPage
        onAuthenticated={(username: string) => {
          setAuthed(username);
          setAuthedState(true);
        }}
      />
    );
  }

  // 027 B2 — mount the EngagementProvider so the engagement aggregate resolves at
  // runtime and the dashboard/consumers read the CURRENT engagement's snapshot
  // (without it the readers fall back to legacy localStorage). The provider is the
  // ONLY caller of /api/engagements. ScopedApp (027 Slice C) gates the first-run
  // gateway vs the engagement-scoped route tree on this same provider.
  return (
    <EngagementProvider>
      <ScopedApp />
    </EngagementProvider>
  );
};

export default App;
